import React, { useState, useEffect } from "react";
import { Icon } from "@iconify/react";
import { Typography } from "../../../Components/Typography";
import Card from "../../../Components/Card";
import Button from "../../../Components/Button";
import { updateUser } from "../../../Utils/apiServices";
import { useAuth } from "../../../Context/authContext";

const NotificationsTab = ({ initialFormData, onUpdate }) => {
  const { user, updateUser: updateAuthUser } = useAuth();
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState({ type: "", text: "" });

  // General notifications
  const [generalNotifications, setGeneralNotifications] = useState({
    ideaAdded: "none",
    meetingScheduled: "none",
    taskAssigned: "none",
    taskOverdue: "none",
    taskStatusUpdated: "none",
  });

  // Summary notifications
  const [summaryNotifications, setSummaryNotifications] = useState({
    dailySummary: "none",
    weeklySummary: "none",
    monthlySummary: "none",
    quarterlySummary: "none",
  });

  // Load notification preferences from user data
  useEffect(() => {
    const loadNotifications = () => {
      const notificationPrefs = user?.notification_preferences || initialFormData?.notification_preferences || {};
      
      setGeneralNotifications({
        ideaAdded: notificationPrefs.ideaAdded || "none",
        meetingScheduled: notificationPrefs.meetingScheduled || "none",
        taskAssigned: notificationPrefs.taskAssigned || "none",
        taskOverdue: notificationPrefs.taskOverdue || "none",
        taskStatusUpdated: notificationPrefs.taskStatusUpdated || "none",
      });

      setSummaryNotifications({
        dailySummary: notificationPrefs.dailySummary || "none",
        weeklySummary: notificationPrefs.weeklySummary || "none",
        monthlySummary: notificationPrefs.monthlySummary || "none",
        quarterlySummary: notificationPrefs.quarterlySummary || "none",
      });
    };

    loadNotifications();
  }, [user, initialFormData]);

  const handleGeneralNotificationChange = (key, value) => {
    setGeneralNotifications((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleSummaryNotificationChange = (key, value) => {
    setSummaryNotifications((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleSave = async () => {
    if (!user?.id) {
      setSaveMessage({ type: "error", text: "User ID not found. Please log in again." });
      return;
    }

    setIsSaving(true);
    setSaveMessage({ type: "", text: "" });

    try {
      const notificationPreferences = {
        ...generalNotifications,
        ...summaryNotifications,
      };

      const apiData = {
        notification_preferences: notificationPreferences,
      };

      const response = await updateUser(user.id, apiData);

      if (response.success) {
        const updatedUserData = {
          notification_preferences: notificationPreferences,
        };
        
        updateAuthUser(updatedUserData);

        const profileToSave = {
          ...initialFormData,
          notification_preferences: notificationPreferences,
        };
        
        if (onUpdate) {
          onUpdate(profileToSave);
        }

        setSaveMessage({ type: "success", text: "Notification preferences updated successfully!" });
        
        setTimeout(() => {
          setSaveMessage({ type: "", text: "" });
        }, 3000);
      } else {
        setSaveMessage({ 
          type: "error", 
          text: response.error || "Failed to update notification preferences. Please try again." 
        });
      }
    } catch (error) {
      console.error("Error updating notification preferences:", error);
      setSaveMessage({ 
        type: "error", 
        text: error.error || "An error occurred while updating your notification preferences." 
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    const notificationPrefs = user?.notification_preferences || initialFormData?.notification_preferences || {};
    
    setGeneralNotifications({
      ideaAdded: notificationPrefs.ideaAdded || "none",
      meetingScheduled: notificationPrefs.meetingScheduled || "none",
      taskAssigned: notificationPrefs.taskAssigned || "none",
      taskOverdue: notificationPrefs.taskOverdue || "none",
      taskStatusUpdated: notificationPrefs.taskStatusUpdated || "none",
    });

    setSummaryNotifications({
      dailySummary: notificationPrefs.dailySummary || "none",
      weeklySummary: notificationPrefs.weeklySummary || "none",
      monthlySummary: notificationPrefs.monthlySummary || "none",
      quarterlySummary: notificationPrefs.quarterlySummary || "none",
    });

    setSaveMessage({ type: "", text: "" });
  };

  const NotificationOption = ({ value, selected, onClick, label }) => {
    return (
      <button
        type="button"
        onClick={onClick}
        className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
          selected === value
            ? "bg-brand-500 text-white"
            : "bg-slate-100 text-slate-700 hover:bg-slate-200"
        }`}
      >
        {label}
      </button>
    );
  };

  const NotificationRow = ({ label, value, onChange }) => {
    return (
      <div className="flex items-center justify-between py-4 border-b border-slate-200 last:border-0">
        <Typography as="label" className="text-sm font-medium text-primary-900 flex-1">
          {label}
        </Typography>
        <div className="flex items-center gap-2">
          <NotificationOption
            value="none"
            selected={value}
            onClick={() => onChange("none")}
            label="None"
          />
          <NotificationOption
            value="in-app"
            selected={value}
            onClick={() => onChange("in-app")}
            label="In-app"
          />
          <NotificationOption
            value="email"
            selected={value}
            onClick={() => onChange("email")}
            label="Email"
          />
        </div>
      </div>
    );
  };

  return (
    <Card className="w-full">
      <div className="flex flex-col gap-6">
        <div className="flex items-start justify-between">
          <div className="flex flex-col gap-1">
            <Typography as="h2" className="text-lg font-bold text-primary-900">
              Notifications
            </Typography>
            <Typography as="p" className="text-sm text-tertiary-600">
              Update your notifications preferences here.
            </Typography>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="secondary"
              onClick={handleCancel}
              disabled={isSaving}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSave}
              disabled={isSaving}
              loading={isSaving}
              className="bg-purple-600 hover:bg-purple-700 focus:ring-purple-500"
            >
              Save
            </Button>
          </div>
        </div>

        {saveMessage.text && (
          <div
            className={`p-4 rounded-lg border ${
              saveMessage.type === "success"
                ? "bg-green-50 border-green-200 text-green-800"
                : "bg-red-50 border-red-200 text-red-800"
            }`}
          >
            <div className="flex items-center gap-2">
              <Icon
                icon={
                  saveMessage.type === "success"
                    ? "lucide:check-circle"
                    : "lucide:alert-circle"
                }
                className={`h-5 w-5 ${
                  saveMessage.type === "success"
                    ? "text-green-600"
                    : "text-red-600"
                }`}
              />
              <Typography as="p" className="text-sm font-medium">
                {saveMessage.text}
              </Typography>
            </div>
          </div>
        )}

        {/* General Notifications Section */}
        <div className="flex flex-row gap-4 justify-between max-w-5xl">
          <div className="flex flex-col gap-1 max-w-xl text-start">
            <Typography as="h3" className="text-base font-bold text-primary-900">
              General notifications
            </Typography>
            <Typography as="p" className="text-sm text-tertiary-600">
              Select when you'll be notified when the following changes occur.
            </Typography>
          </div>
          <div className="bg-white rounded-lg border border-slate-200 p-4 w-full">
            <NotificationRow
              label="An idea has been added for review"
              value={generalNotifications.ideaAdded}
              onChange={(value) => handleGeneralNotificationChange("ideaAdded", value)}
            />
            <NotificationRow
              label="A meeting has been scheduled"
              value={generalNotifications.meetingScheduled}
              onChange={(value) => handleGeneralNotificationChange("meetingScheduled", value)}
            />
            <NotificationRow
              label="I'm assigned a task"
              value={generalNotifications.taskAssigned}
              onChange={(value) => handleGeneralNotificationChange("taskAssigned", value)}
            />
            <NotificationRow
              label="A task is overdue"
              value={generalNotifications.taskOverdue}
              onChange={(value) => handleGeneralNotificationChange("taskOverdue", value)}
            />
            <NotificationRow
              label="A task status is updated"
              value={generalNotifications.taskStatusUpdated}
              onChange={(value) => handleGeneralNotificationChange("taskStatusUpdated", value)}
            />
          </div>
        </div>

        {/* Summary Notifications Section */}
        <div className="flex flex-row gap-4 justify-between max-w-5xl">
          <div className="flex flex-col gap-1 max-w-xl text-start">
            <Typography as="h3" className="text-base font-bold text-primary-900">
              Summary notifications
            </Typography>
            <Typography as="p" className="text-sm text-tertiary-600">
              Select when you'll be notified when the following summaries or report are ready.
            </Typography>
          </div>
          <div className="bg-white rounded-lg border border-slate-200 p-4 w-full">
            <NotificationRow
              label="Daily summary"
              value={summaryNotifications.dailySummary}
              onChange={(value) => handleSummaryNotificationChange("dailySummary", value)}
            />
            <NotificationRow
              label="Weekly summary"
              value={summaryNotifications.weeklySummary}
              onChange={(value) => handleSummaryNotificationChange("weeklySummary", value)}
            />
            <NotificationRow
              label="Monthly summary"
              value={summaryNotifications.monthlySummary}
              onChange={(value) => handleSummaryNotificationChange("monthlySummary", value)}
            />
            <NotificationRow
              label="Quarterly summary"
              value={summaryNotifications.quarterlySummary}
              onChange={(value) => handleSummaryNotificationChange("quarterlySummary", value)}
            />
          </div>
        </div>
      </div>
    </Card>
  );
};

export default NotificationsTab;

